import { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';

const POLL_INTERVAL_MS = 500;
// Tempo minimo que cada estado fica visivel antes de trocar para o proximo —
// os intervalos reais entre tentativas (150-200ms) sao rapidos demais para o
// olho humano perceber como passos distintos, entao a UI faz esse pacing sem
// alterar o tempo real de retry no backend.
const MIN_STEP_DISPLAY_MS = 800;
// Tetos do polling: sem o registro da operacao (404 em loop) desiste cedo;
// com registro, para de qualquer forma um pouco depois do maior tempo possivel
// de uma operacao (ver COMANDO_REQUEST_TIMEOUT_MS), para nunca ficar vivo.
const NO_RECORD_GIVE_UP_MS = 10000;
const MAX_POLL_DURATION_MS = 40000;

const generateOperationId = () => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `op-${Date.now()}-${Math.random().toString(16).slice(2)}`;
};

/**
 * Gera um operationId e da polling em GET /api/exaustores/comandos/:id/progresso
 * a cada ~500ms, refletindo o progresso real de um comando ou consulta de
 * status (retry acontecendo no backend). Para no estado terminal.
 */
const useOperationProgress = () => {
  const [progress, setProgress] = useState(null);
  const abortControllerRef = useRef(null);
  const pollTimeoutRef = useRef(null);
  const pendingTimeoutRef = useRef(null);
  const pendingDataRef = useRef(null);
  const activeRef = useRef(false);
  const lastShownAtRef = useRef(0);
  const startedAtRef = useRef(0);
  const recordSeenRef = useRef(false);

  const stopPolling = useCallback(() => {
    activeRef.current = false;
    if (pollTimeoutRef.current) {
      clearTimeout(pollTimeoutRef.current);
      pollTimeoutRef.current = null;
    }
    if (pendingTimeoutRef.current) {
      clearTimeout(pendingTimeoutRef.current);
      pendingTimeoutRef.current = null;
    }
    abortControllerRef.current?.abort();
    abortControllerRef.current = null;
  }, []);

  const commit = useCallback((data) => {
    lastShownAtRef.current = Date.now();
    setProgress(data);
  }, []);

  const applyProgress = useCallback((data) => {
    if (data.terminal) {
      // Estado final: sempre mostra na hora, sem pacing.
      if (pendingTimeoutRef.current) {
        clearTimeout(pendingTimeoutRef.current);
        pendingTimeoutRef.current = null;
      }
      commit(data);
      return;
    }

    const elapsed = Date.now() - lastShownAtRef.current;
    if (elapsed >= MIN_STEP_DISPLAY_MS) {
      commit(data);
      return;
    }

    pendingDataRef.current = data;
    if (pendingTimeoutRef.current) {
      clearTimeout(pendingTimeoutRef.current);
    }
    pendingTimeoutRef.current = setTimeout(() => {
      pendingTimeoutRef.current = null;
      commit(pendingDataRef.current);
    }, MIN_STEP_DISPLAY_MS - elapsed);
  }, [commit]);

  const poll = useCallback((apiUrl, operationId, condominioId) => {
    if (!activeRef.current) return;

    const elapsed = Date.now() - startedAtRef.current;
    if (elapsed > MAX_POLL_DURATION_MS || (!recordSeenRef.current && elapsed > NO_RECORD_GIVE_UP_MS)) {
      activeRef.current = false;
      return;
    }

    abortControllerRef.current = new AbortController();

    axios.get(`${apiUrl}/api/exaustores/comandos/${operationId}/progresso`, {
      headers: { 'x-condominio-id': condominioId },
      signal: abortControllerRef.current.signal
    }).then((response) => {
      if (!activeRef.current) return;

      const dados = response.data?.dados;
      if (dados) {
        recordSeenRef.current = true;
        applyProgress(dados);
        if (dados.terminal) {
          activeRef.current = false;
          return;
        }
      }

      if (activeRef.current) {
        pollTimeoutRef.current = setTimeout(() => poll(apiUrl, operationId, condominioId), POLL_INTERVAL_MS);
      }
    }).catch((err) => {
      if (err.name === 'AbortError' || err.name === 'CanceledError' || err.code === 'ERR_CANCELED') {
        return;
      }
      // 404 no primeiro instante (operacao ainda nao registrada no backend) —
      // apenas continua tentando.
      if (activeRef.current) {
        pollTimeoutRef.current = setTimeout(() => poll(apiUrl, operationId, condominioId), POLL_INTERVAL_MS);
      }
    });
  }, [applyProgress]);

  const start = useCallback((apiUrl, condominioId) => {
    stopPolling();
    const operationId = generateOperationId();
    activeRef.current = true;
    lastShownAtRef.current = 0;
    startedAtRef.current = Date.now();
    recordSeenRef.current = false;
    setProgress(null);
    poll(apiUrl, operationId, condominioId);
    return operationId;
  }, [poll, stopPolling]);

  const reset = useCallback(() => {
    stopPolling();
    setProgress(null);
  }, [stopPolling]);

  // Ao desmontar (usuario saiu da pagina) nao deixa o polling rodando ate o
  // fim da operacao: cancela a requisicao em voo e os timers pendentes.
  useEffect(() => () => stopPolling(), [stopPolling]);

  return { progress, start, stop: stopPolling, reset };
};

export default useOperationProgress;
